import {proposalService} from 'app/services'
import _t, {_} from 'i18n'
import {useSnackbar} from 'notistack'
import {useCallback, useState} from 'react'

const useUpdateOpportunity = ({id, status: opportunityStatus, callback}) => {
  const {enqueueSnackbar} = useSnackbar()

  const [status, setStatus] = useState(opportunityStatus)
  const [loading, setLoading] = useState(false)

  const completeProposal = useCallback(
    async (proposalId) => {
      try {
        setLoading(true)

        await proposalService.completeProposal({id: proposalId})

        setStatus('completed')

        enqueueSnackbar(_`Oportunidade concluída!`, {
          variant: 'success',
        })

        if (callback) callback()
      } catch (error) {
        var message = error?.response?.data?.message
          ? error.response.data.message
          : _t('Ocorreu um erro ao tentar perder / ganhar / concretizar a oportunidade #{id}', {id: proposalId})
        enqueueSnackbar(message, {variant: 'error'})
      } finally {
        setLoading(false)
      }
    },
    [enqueueSnackbar],
  )

  const lostProposal = useCallback(
    async (proposalId) => {
      try {
        setLoading(true)

        await proposalService.lostProposal({id: proposalId})

        setStatus('lost')

        enqueueSnackbar(_`Oportunidade perdida!`, {
          variant: 'success',
        })

        if (callback) callback()
      } catch (error) {
        var message = error?.response?.data?.message
          ? error.response.data.message
          : _t('Ocorreu um erro ao tentar perder a oportunidade #{id}', {id: proposalId})
        enqueueSnackbar(message, {variant: 'error'})
      } finally {
        setLoading(false)
      }
    },
    [enqueueSnackbar],
  )

  const reOpenProposal = useCallback(
    async (proposalId) => {
      try {
        setLoading(true)

        await proposalService.reOpenProposal({id: proposalId})

        setStatus('open')

        enqueueSnackbar(status === 'pending' ? _`Oportunidade aprovada e ativada.` : _`Oportunidade re-ativada!`, {
          variant: 'success',
        })

        if (callback) callback()
      } catch (error) {
        var message = error?.response?.data?.message
          ? error.response.data.message
          : _t('Ocorreu um erro ao tentar re-ativar a oportunidade #{id}', {id: proposalId})
        enqueueSnackbar(message, {variant: 'error'})
      } finally {
        setLoading(false)
      }
    },
    [enqueueSnackbar],
  )

  const deleteProposal = useCallback(
    async (proposalId) => {
      try {
        setLoading(true)

        if (!window.confirm(_`Tem certeza? Essa ação não poderá ser desfeita`)) {
          return false
        }

        await proposalService.deleteProposal({id: proposalId})

        enqueueSnackbar(_`Oportunidade removida!`, {
          variant: 'success',
        })

        if (callback) callback()
      } catch (error) {
        var message = error?.response?.data?.message
          ? error.response.data.message
          : _t('Ocorreu um erro ao tentar excluir a oportunidade #{id}', {id: proposalId})
        enqueueSnackbar(message, {variant: 'error'})
      } finally {
        setLoading(false)
      }
    },
    [enqueueSnackbar],
  )

  const action = useCallback(
    (newStatus) => {
      if (newStatus === 'delete') {
        return deleteProposal(id)
      }

      if (newStatus && newStatus === 'lost') {
        lostProposal(id)
      } else if (status === 'open') {
        completeProposal(id)
      } else {
        // Use the same reopen logic for both 'completed', 'lost', and 'pending' statuses
        reOpenProposal(id)
      }
    },
    [completeProposal, id, reOpenProposal, status],
  )

  return {
    action,
    loading,
    status,
  }
}

export default useUpdateOpportunity
